/**
 * Daisies — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_motifs_ii`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const daisiesPack: ToilePack = {
    slug: 'daisies',
    category: 'motif',
    name: 'Daisies',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'single', label: 'Single daisy', subject: 'A single daisy with white petals and a yellow centre on a slender stem with two small leaves, drawn whole on a plain ground.'},
        {key: 'chain', label: 'Daisy chain', subject: "A daisy chain looped into a circle, each flower's stem threaded through the next, a few petals loose, on a plain ground."},
        {key: 'meadow', label: 'Daisy meadow', subject: 'A meadow of daisies filling the frame from close large flowers at the bottom to a haze of tiny ones at the top, a soft sky, a single bee.'},
    ],
    styles: [
        {key: 'botanical', label: 'Botanical line', style: 'botanical line illustration, fine black ink, light stipple, white ground', palette: 'fine black ink only, no other colour', framing: 'centred on a plain white background with a wide even margin'},
        {key: 'retro', label: 'Flat retro', style: 'flat 1970s retro daisy illustration, bold simplified petals, warm palette', palette: 'a 1970s palette of mustard, burnt orange and cream', framing: 'centred on a warm cream background with bold flat shapes'},
        {key: 'watercolour', label: 'Watercolour', style: 'soft watercolour illustration, gentle washes, white paper', palette: 'soft white and yellow washes with a little sage green', framing: 'set on a plain white paper background, the wash fading toward a soft margin'},
    ],
    directions: ['a sunflower instead', 'as a hair crown', 'for a spring card'],
    directionHint: 'try: a sunflower instead',
    seo: {
        title: 'Daisy art: single, chain, meadow in three styles',
        metaDescription: 'Nine daisy illustrations — a single daisy, a looped chain and a meadow — in botanical line, 1970s retro and watercolour styles.',
        h1: 'Daisy drawings: single, chain, meadow in three styles',
        tagline: 'Nine renders, three daisy readings, three looks each — botanical to retro.',
        intro: 'The plainest flower left off the last motif wave: a single daisy on its stem, a chain looped into a circle, and a meadow of them running to the horizon. Each renders as fine botanical line, flat 1970s retro shapes, or soft watercolour washes. Ask for a sunflower instead or a hair-crown version and the same three looks carry it.',
        useCases: [
            'a spring or wedding card',
            'a botanical print or poster',
            "a nursery or kids' room wall",
            'a social media spring post',
        ],
        faq: [
            {q: 'What size do these print at?', a: 'Each is composed in a tall portrait crop, suited to a print, a card or a poster.'},
            {q: 'Can I get a sunflower or a hair-crown version?', a: 'Yes — the direction field takes any variation, and the same three styles render it.'},
            {q: "What's the difference between the three styles?", a: 'Botanical line is fine black ink with light stipple, retro is bold 1970s mustard and orange shapes, and watercolour is a soft yellow-and-white wash.'},
            {q: 'Is this a real photograph of a daisy?', a: 'No — every image is an illustration, drawn in the chosen style, not a photo.'},
        ],
    },
}
